import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { Table, notification } from 'antd';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ColumnsType } from 'antd/es/table';
import { LoadingOutlined, ReloadOutlined, MenuOutlined, PrinterOutlined, DownloadOutlined } from '@ant-design/icons';

import printJS from 'print-js';
import { saveAs } from 'file-saver';

import Toolbar from '@controls/toolbar/toolbar';

import IBaseComponentProps from '@components/base-component-props';
import FormHeader from '@controls/form-header/form-header';
import Processing from '@controls/processing';

import { exception } from '@extensions/notification';
import { dataURItoBlob } from '@extensions/utils';

import { userLoaded } from '@store/actions';
import { useAppDispatch } from '@store/hooks';

import { IRouteSheetItem } from '@entities/route-sheet-item';
import { IShipmentJournal } from '@entities/shipment-journal';

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
    'data-row-key': string;
}

const RouteSheet = (props: IBaseComponentProps) => {
    const { serverFetch } = props;

    const [api, contextHolder] = notification.useNotification();

    const { journalId } = useParams();

    const d = useAppDispatch();

    const [routeSheet, setRouteSheet] = useState<Array<IRouteSheetItem>>([]);
    const [journal, setJournal] = useState<IShipmentJournal>();

    const [refreshRequired, setRefreshRequired] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [startPrint, setStartPrint] = useState<boolean>(false);

    useEffect(() => {
        if (!refreshRequired) return;

        setRefreshRequired(false);
        init();
    }, [refreshRequired]);

    const init = () => {
        let cleanup = false;

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch(`routesheets/${journalId}`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения маршрута', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch(`shipmentjournals/${journalId}`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения журнала отправки', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setRouteSheet(result[0][0]);
                setJournal(result[0][1]);

                setLoading(false);
            });
        };

        fetchData();
        return () => {
            cleanup = true;
        };
    };

    const onDownload = () => {
        if (!document) return;

        serverFetch(`routesheets/print`, { method: 'POST', bodyData: journalId })
            .then((data: any) => {
                const fileBody = `data:${data.contentType};base64,${data.fileContents}`;
                saveAs(dataURItoBlob(fileBody), data.fileDownloadName);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка загрузки маршрутного листа', ex, () => d(userLoaded(undefined)));
            });
    };

    const onPrint = () => {
        setStartPrint(true);

        serverFetch(`routesheets/print`, { method: 'POST', bodyData: journalId })
            .then((data: any) => {
                const fileBody = `data:${data.contentType};base64,${data.fileContents}`;

                const blobURL = URL.createObjectURL(dataURItoBlob(fileBody));
                printJS(blobURL);

                setStartPrint(false);
            })
            .catch((ex: any) => {
                setStartPrint(false);
                exception(api, 'Ошибка печати маршрутного листа', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Скачать',
                        key: 'downloadRouteSheet',
                        disabled: loading,
                        icon: <DownloadOutlined />,
                        onClick: () => onDownload(),
                    },
                    {
                        label: 'Печать',
                        key: 'printRouteSheet',
                        disabled: loading || startPrint,
                        icon: <PrinterOutlined />,
                        onClick: () => onPrint(),
                    },
                ]}
            />
        );
    };

    const Row = ({ children, ...props }: RowProps) => {
        const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
            id: props['data-row-key'],
        });

        const style: React.CSSProperties = {
            ...props.style,
            transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 })?.replace(/translate3d\(([^,]+),/, 'translate3d(0,'),
            transition,
            ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
        };

        return (
            <tr {...props} ref={setNodeRef} style={style} {...attributes}>
                {React.Children.map(children, (child) => {
                    if ((child as React.ReactElement).key === 'sort') {
                        return React.cloneElement(child as React.ReactElement, {
                            children: (
                                <MenuOutlined ref={setActivatorNodeRef} style={{ touchAction: 'none', cursor: 'move' }} {...listeners} />
                            ),
                        });
                    }
                    return child;
                })}
            </tr>
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<IRouteSheetItem> = [
            {
                key: 'sort',
                width: 40,
            },
            {
                title: 'Город доставки',
                dataIndex: 'cityName',
                width: 200,
            },
            {
                title: 'Адрес',
                dataIndex: 'address',
            },
        ];

        const onDragEnd = ({ active, over }: DragEndEvent) => {
            if (active.id !== over?.id) {
                let copyRouteSheet = [...routeSheet];

                const activeIndex = copyRouteSheet.findIndex((i) => i.sequence === active.id);
                const overIndex = copyRouteSheet.findIndex((i) => i.sequence === over?.id);

                copyRouteSheet = arrayMove(copyRouteSheet, activeIndex, overIndex);

                serverFetch(`routesheets/${journalId}`, { method: 'POST', bodyData: copyRouteSheet })
                    .then(() => {
                        setRouteSheet(copyRouteSheet);
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка изменения маршрута', ex, () => d(userLoaded(undefined)));
                    });
            }
        };

        return (
            <DndContext onDragEnd={onDragEnd}>
                <SortableContext items={routeSheet.map((i) => i.sequence)} strategy={verticalListSortingStrategy}>
                    <Table
                        components={{
                            body: {
                                row: Row,
                            },
                        }}
                        rowKey='sequence'
                        size='small'
                        columns={columns}
                        dataSource={routeSheet}
                        loading={{
                            spinning: loading,
                            indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                        }}
                        pagination={false}
                        scroll={{ y: `calc(100vh - 250px)` }}
                    />
                </SortableContext>
            </DndContext>
        );
    };

    return (
        <>
            <FormHeader title={`Маршрутный лист "${journal?.journalNumber}"`} />
            {renderToolbar()}
            {renderTable()}

            {startPrint && <Processing title='Печать...' />}

            {contextHolder}
        </>
    );
};

export default RouteSheet;
